import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import {
    type AuditChangeCell,
    type AuditChangeRow,
    type AuditLogFilters,
    useAuditText,
} from '@/domain/auditLog';

import DetailsFilterButton from './DetailsFilterButton';

import styles from './ChangesTable.module.scss';

interface ChangesTableProps {
    rows: readonly AuditChangeRow[];
    onFilter: (filters: AuditLogFilters) => void;
}

interface ValueCellProps {
    cell: AuditChangeCell | null;
    field: string;
    nameKey: 'auditLog.details.filterBefore' | 'auditLog.details.filterAfter';
    onFilter: (filters: AuditLogFilters) => void;
}

function ValueCell({ cell, field, nameKey, onFilter }: ValueCellProps) {
    const { t } = useTranslation();
    const render = useAuditText();
    if (cell === null) return null;
    const text = render(cell.text);
    const { filter } = cell;
    return (
        <span className={styles.content}>
            <span className={styles.text} title={cell.title ?? undefined}>
                {text}
            </span>
            {filter === null ? null : (
                <DetailsFilterButton
                    name={t(nameKey, { field, value: text })}
                    onFilter={() => onFilter(filter)}
                />
            )}
        </span>
    );
}

function FieldCell(props: { row: AuditChangeRow; onFilter: (filters: AuditLogFilters) => void }) {
    const { row, onFilter } = props;
    const { t } = useTranslation();
    const { field, filter } = row;
    return (
        <span className={styles.content}>
            <span className={styles.field}>{field}</span>
            {filter === null ? null : (
                <DetailsFilterButton
                    name={t('auditLog.details.filterValue', {
                        label: t('auditLog.chips.label.changedField'),
                        value: field,
                    })}
                    onFilter={() => onFilter(filter)}
                />
            )}
        </span>
    );
}

/**
 * Every recorded field, unchanged ones muted. A delete snapshot fills Before only; a
 * value with no before state, After only.
 */
export default function ChangesTable({ rows, onFilter }: ChangesTableProps) {
    const { t } = useTranslation();

    const columns: DataTableColumnDef<AuditChangeRow>[] = [
        {
            id: 'field',
            header: t('auditLog.details.field'),
            meta: {
                rowHeader: true,
                headerClassName: styles.header,
                cellClassName: classNames(styles.cell, styles.fieldCell),
            },
            cell: ({ row }) => <FieldCell row={row.original} onFilter={onFilter} />,
        },
        {
            id: 'before',
            header: t('auditLog.details.before'),
            meta: { headerClassName: styles.header, cellClassName: styles.cell },
            cell: ({ row }) => (
                <ValueCell
                    cell={row.original.before}
                    field={row.original.field}
                    nameKey="auditLog.details.filterBefore"
                    onFilter={onFilter}
                />
            ),
        },
        {
            id: 'after',
            header: t('auditLog.details.after'),
            meta: { headerClassName: styles.header, cellClassName: styles.cell },
            cell: ({ row }) => (
                <ValueCell
                    cell={row.original.after}
                    field={row.original.field}
                    nameKey="auditLog.details.filterAfter"
                    onFilter={onFilter}
                />
            ),
        },
    ];

    return (
        <DataTable
            data={[...rows]}
            columns={columns}
            getRowId={(row) => row.field}
            density="compact"
            enableSorting={false}
            hover={false}
            tableClassName={styles.table}
            getRowProps={(row) => ({
                className: classNames({ [styles.unchanged]: !row.original.changed }),
            })}
        />
    );
}
